import { dictionaryApi } from './dictionaryClient';
import { normalizeBarnaSamuha, type BarnaSamuha } from './barnaSamuhaApi';
import { normalizeAssociatedBarna, type AssociatedBarna } from './pratyaharApi';

export type { AssociatedBarna };

export type PadachhedBarnaSamuhaLink = {
  id: number;
  ashtadhyayiPadachhedId: number;
  barnaSamuhaId: number;
  selectedBarnas: AssociatedBarna[];
  barnaSamuha: BarnaSamuha | null;
};

export type CreatePadachhedBarnaSamuhaLinkBody = {
  ashtadhyayiPadachhedId: number;
  barnaSamuhaId: number;
  selectedBarnas: AssociatedBarna[];
};

export type UpdatePadachhedBarnaSamuhaLinkBody = {
  selectedBarnas?: AssociatedBarna[];
};

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

export function normalizePadachhedBarnaSamuhaLink(raw: unknown): PadachhedBarnaSamuhaLink | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiPadachhedId =
    num(raw, 'ashtadhyayiPadachhedId') ?? num(raw, 'ashtadhyayi_padachhed_id');
  const barnaSamuhaId = num(raw, 'barnaSamuhaId') ?? num(raw, 'barna_samuha_id');
  if (id == null || ashtadhyayiPadachhedId == null || barnaSamuhaId == null) return null;

  const selectedRaw = raw.selectedBarnas ?? raw.selected_barnas;
  const selectedBarnas = Array.isArray(selectedRaw)
    ? selectedRaw.map(normalizeAssociatedBarna).filter((x): x is AssociatedBarna => x != null)
    : [];

  const barnaSamuhaRaw = raw.barnaSamuha ?? raw.barna_samuha;
  const barnaSamuha = barnaSamuhaRaw ? normalizeBarnaSamuha(barnaSamuhaRaw) : null;

  return {
    id,
    ashtadhyayiPadachhedId,
    barnaSamuhaId,
    selectedBarnas,
    barnaSamuha,
  };
}

function normalizeList(data: unknown): PadachhedBarnaSamuhaLink[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizePadachhedBarnaSamuhaLink)
    .filter((x): x is PadachhedBarnaSamuhaLink => x != null);
}

export async function listPadachhedBarnaSamuhaLinks(filters?: {
  ashtadhyayiPadachhedId?: number;
  barnaSamuhaId?: number;
}): Promise<PadachhedBarnaSamuhaLink[]> {
  const params: Record<string, number> = {};
  if (filters?.ashtadhyayiPadachhedId != null) {
    params.ashtadhyayiPadachhedId = filters.ashtadhyayiPadachhedId;
  }
  if (filters?.barnaSamuhaId != null) {
    params.barnaSamuhaId = filters.barnaSamuhaId;
  }
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-padachhed-barna-samuha', {
    params: Object.keys(params).length > 0 ? params : undefined,
  });
  return normalizeList(res.data);
}

export async function getPadachhedBarnaSamuhaLink(id: number): Promise<PadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-padachhed-barna-samuha/${id}`);
  const row = normalizePadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–barna samuha link response');
  return row;
}

export async function createPadachhedBarnaSamuhaLink(
  body: CreatePadachhedBarnaSamuhaLinkBody
): Promise<PadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-padachhed-barna-samuha', body);
  const row = normalizePadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–barna samuha link response');
  return row;
}

export async function updatePadachhedBarnaSamuhaLink(
  id: number,
  body: UpdatePadachhedBarnaSamuhaLinkBody
): Promise<PadachhedBarnaSamuhaLink> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-padachhed-barna-samuha/${id}`, body);
  const row = normalizePadachhedBarnaSamuhaLink(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid padachhed–barna samuha link response');
  return row;
}

export async function deletePadachhedBarnaSamuhaLink(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-padachhed-barna-samuha/${id}`);
}
